import { Component, OnInit } from "@angular/core";
import { VehicleDecalService } from '../../core/service/vehicle-decal.service';
import { IDecalExpiration, IDecalNumberRegistration, IVehicleDecal } from '../../core/models/vehicle-decal.model';
import { Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import {
  hasDecalSelectionRoleSelector,
  isImpersonatingSelector,
  isLoadingUserOrRolesSelector
} from "../../core/store/auth/auth.selectors";
import { Observable } from "rxjs";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { IVehicle } from "../../core/models/vehicle.model";
import { IVehicleModel } from "../../core/models/vehicle-model.model";
import { IStateCode } from '../../core/models/statecode.model';
import { IVehicleMake, IVehicleType } from "../../core/models/vehicle-make.model";
import { IVehicleColor } from "../../core/models/vehicle-color.model";
import { VehicleService } from "../../core/service/vehicle.service";
import { VehicleMakeService } from "../../core/service/vehicle-make.service";
import { VehicleColorService } from "../../core/service/vehicle-color.service";
import {
  getExpirableDecalsSelector,
  getRequestedDecalsSelector,
  getVehicleDecalsSelector
} from "../../core/store/vehicle-decals/vehicle-decals.selectors";
import { clearVehicleDecalsAction } from "../../core/store/vehicle-decals/vehicle-decals.actions";

const defaultNavLinks: any = {
  decals: false
}

@Component({
    templateUrl: 'decals.component.html',
    styleUrls: ['decals.component.scss'],
    standalone: false
})
export class DecalsComponent implements OnInit {

  readonly code = 'PARK1';
  readonly ISSUE_DISPOSITION = 'Issued';

  // Define a Set to store the IDs of the decals for which the vehicle list should be shown
  public decalIdsToShowVehicles = new Set<number>();

  public vehicleDecals$: Observable<IVehicleDecal[] | undefined>;
  public requestedDecals$: Observable<IVehicleDecal[]>;
  public expirableDecals$: Observable<IVehicleDecal[]>;

  agreedGeneral = false;
  agreedPayroll = false;

  public isLoading$: Observable<boolean>;
  public hasDecalSelectionRole$: Observable<boolean>;
  public isImpersonating$: Observable<boolean>;

  public isModalOpen: boolean;
  public selectedDecal: IVehicleDecal;
  public decalNumber: string = '';
  public vehicleTypes: IVehicleType[] = [];
  public vehicleMakes: IVehicleMake[] = [];
  public vehicleModels: IVehicleModel[] = [];
  public vehicleColors: IVehicleColor[] = [];
  public stateCodes: IStateCode[] = [];
  public filteredMakes: any[] = [];
  public userVehicleList: IVehicle[] = [];
  public archivedVehicleList: IVehicle[] = [];
  public electricVehicles = ['N', 'Y'];
  public selectedVehicleOption: string = '';
  public isRegisterModalOpen: boolean = false;
  public paidInPerson: boolean = false;
  public isExpireModalOpen: boolean = false;
  public clearDecalNumber: boolean = false;
  public modalTitle: string;
  public formSubmitted: boolean = false;
  public stagedVehicle: IVehicle;
  public showOtherModel: boolean = false;
  public showLess: boolean = false;

  public vehicleEditForm = this.formBuilder.group({
    vehicleType: [{ value: this.selectedVehicleOption, disabled: false }, Validators.required],
    vehicleMake: [{ value: this.selectedVehicleOption, disabled: false }, Validators.required],
    vehicleModel: [{ value: this.selectedVehicleOption, disabled: false }, Validators.required],
    otherModel: '',
    vehicleColor: '',
    yearOfVehicle: [{ value: this.selectedVehicleOption, disabled: false }, Validators.required],
    licenseNumber: '',
    vehicleStateCode: '',
    vehicleInsured: false,
    electricVehicle: this.selectedVehicleOption,
    active: '',
    printModel: ''
  });

  constructor(private vehicleDecalService: VehicleDecalService,
    private router: Router,
    private store: Store,
    private vehicleMakeService: VehicleMakeService,
    private vehicleService: VehicleService,
    private vehicleColorService: VehicleColorService,
    private formBuilder: UntypedFormBuilder) { }

  ngOnInit() {
    this.getUserVehicleDecals();
    this.getVehicles();
    this.getVehicleTypes();
    this.getVehicleMakes();
    this.getVehicleColors();
    this.getStateCodes();
    this.isLoading$ = this.store.pipe(select(isLoadingUserOrRolesSelector));
    this.showOtherModel = false;
    this.hasDecalSelectionRole$ = this.store.pipe(select(hasDecalSelectionRoleSelector));
    this.isImpersonating$ = this.store.pipe(select(isImpersonatingSelector));
  }

  /**
   * public method to determine if vehicles for a given decal should be shown.
   * @public
   */
  public showVehiclesForDecal(decal: IVehicleDecal): boolean {
    return this.decalIdsToShowVehicles.has(decal.decalId);
  }

  /**
   * public method to toggle showing/hiding the vehicle list for a given decal.
   * @public
   * */
  public toggleVehicleDetails(decal: IVehicleDecal): void {
    if (this.decalIdsToShowVehicles.has(decal.decalId)) {
      this.decalIdsToShowVehicles.delete(decal.decalId);
    } else {
      this.decalIdsToShowVehicles.add(decal.decalId);
    }
  }

  public get form() {
    return this.vehicleEditForm.controls;
  }

  public getUserVehicleDecals(): void {
    this.vehicleDecals$ = this.store.pipe(select(getVehicleDecalsSelector));
    this.requestedDecals$ = this.store.pipe(select(getRequestedDecalsSelector));
    this.expirableDecals$ = this.store.pipe(select(getExpirableDecalsSelector));
  }

  redirectOrderDecals() {
    this.router.navigate(['order-process']);
  }

  public get vehicleType(): IVehicleType {
    return this.vehicleEditForm.get('vehicleType')?.value;
  }

  public get vehicleMake(): IVehicleMake {
    return this.vehicleEditForm.get('vehicleMake')?.value;
  }

  public get vehicleModel(): IVehicleModel {
    return this.vehicleEditForm.get('vehicleModel')?.value;
  }

  public get otherModel(): string {
    return this.vehicleEditForm.get('otherModel')?.value;
  }

  public get vehicleColor(): IVehicleColor {
    return this.vehicleEditForm.get('vehicleColor')?.value;
  }

  public get yearOfVehicle(): number {
    return this.vehicleEditForm.get('yearOfVehicle')?.value;
  }

  public get licenseNumber(): string {
    return this.vehicleEditForm.get('licenseNumber')?.value;
  }

  public get vehicleStateCode(): string {
    return this.vehicleEditForm.get('vehicleStateCode')?.value;
  }

  public get vehicleInsured(): boolean {
    return this.vehicleEditForm.get('vehicleInsured')?.value;
  }

  public set vehicleInsured(value: boolean) {
    this.vehicleEditForm.get('vehicleInsured')?.setValue(value);
  }

  public get electricVehicle(): string {
    return this.vehicleEditForm.get('electricVehicle')?.value;
  }

  public changeVehicleColor(vcolor: IVehicleColor) {
    this.vehicleEditForm.get('vehicleEditForm')?.setValue(vcolor);
  }

  public changeStateCode(scode: string) {
    this.vehicleEditForm.get('vehicleEditForm')?.setValue(scode);
  }

  public changeElectricVehicle(evehicle: string) {
    this.vehicleEditForm.get('vehicleEditForm')?.setValue(evehicle);
  }

  public getVehicleTypes(): void {
    this.vehicleMakeService.getVehicleTypes().subscribe((type: IVehicleType[]) => {
      this.vehicleTypes = type;
    });
  }

  public getVehicleMakes(): void {
    this.vehicleMakeService.getVehicleMakes().subscribe((make: IVehicleMake[]) => {
      this.vehicleMakes = make;
    });
  }

  public getAllModelsForAllMakes(): void {
    this.vehicleMakeService.getAllModelsForAllMakes().subscribe((model: IVehicleModel[]) => {
      this.vehicleModels = model;
    });
  }

  public getVehicleColors(): void {
    this.vehicleColorService.getVehicleColors().subscribe((color: IVehicleColor[]) => {
      this.vehicleColors = color;
    });
  }

  public getStateCodes(): void {
    this.vehicleService.getStateCodes().subscribe((code: IStateCode[]) => {
      this.stateCodes = code;
    });
  }

  public getVehicles(): void {
    this.vehicleService.getVehicles().subscribe((userVehicleList: IVehicle[]) => {
      this.userVehicleList = userVehicleList;
      this.userVehicleList = userVehicleList.filter(vehicle => vehicle.active == 'Y');
      this.archivedVehicleList = userVehicleList.filter(vehicle => vehicle.active == 'N');
      this.vehicleEditForm.setValue({
        vehicleType: this.selectedVehicleOption,
        vehicleMake: this.selectedVehicleOption,
        vehicleModel: this.selectedVehicleOption,
        otherModel: '',
        vehicleColor: this.selectedVehicleOption,
        yearOfVehicle: '',
        licenseNumber: '',
        vehicleStateCode: this.selectedVehicleOption,
        vehicleInsured: false,
        electricVehicle: this.selectedVehicleOption,
        active: '',
        printModel: ''
      })
    });
  }

  public submitForm(): void {
    if (this.stagedVehicle) {
      this.vehicleService.updateVehicle({
        ...this.stagedVehicle,
        vehicleType: this.vehicleType,
        make: this.vehicleMake,
        model: this.vehicleModel,
        otherModel: this.otherModel,
        vehicleYear: this.yearOfVehicle,
        color: this.vehicleColor,
        licenseNumber: this.licenseNumber,
        stateCode: this.vehicleStateCode,
        insured: this.vehicleInsured ? 'Y' : 'N',
        electric: this.electricVehicle
      }).subscribe((updatedVehicle: IVehicle) => {
        this.userVehicleList = this.userVehicleList.map((vehicle: IVehicle) => vehicle.id === updatedVehicle.id ? updatedVehicle : vehicle);
        this.store.dispatch(clearVehicleDecalsAction());
        this.getUserVehicleDecals();
        this.closeModal();
      });

    }
  }

  public openModal(vehicle: IVehicle) {
    this.isModalOpen = true;
    this.modalTitle = 'Edit Vehicle';
    this.vehicleMakeService.getModels(vehicle.make.id).subscribe((models) => {
      this.vehicleModels = models;
      if (!vehicle.make.text?.includes("Other") && !vehicle.make.text?.includes("other")) {
        if (this.vehicleMakeService.addOtherModel(vehicle.make) != null) {
          this.vehicleModels.push(this.vehicleMakeService.addOtherModel(vehicle.make)!);
        }
      }
      this.showOtherModel = (vehicle.model.text!.includes("Other") || vehicle.model.text!.includes("other"));

      this.stagedVehicle = vehicle;
      this.filteredMakes = this.vehicleMakes;

      let indexOfVehicleType = this.vehicleTypes.findIndex(vtIndex => vtIndex.id === vehicle.vehicleType.id);
      let indexOfVehicleMake = this.filteredMakes.findIndex(mkIndex => mkIndex.id === vehicle.make.id);
      let indexOfVehicleModel = this.vehicleModels.findIndex(modIndex => modIndex.id === vehicle.model.id);
      let indexOfVehicleColor = this.vehicleColors.findIndex(cIndex => cIndex.id === vehicle.color.id);

      this.vehicleEditForm.get('vehicleType')?.disable();
      this.vehicleEditForm.get('vehicleMake')?.disable();
      this.vehicleEditForm.get('vehicleModel')?.disable();
      this.vehicleEditForm.get('yearOfVehicle')?.disable();

      this.vehicleEditForm.setValue({
        vehicleType: this.vehicleTypes[indexOfVehicleType],
        vehicleMake: this.filteredMakes[indexOfVehicleMake],
        vehicleModel: this.vehicleModels[indexOfVehicleModel],
        otherModel: vehicle.otherModel,
        vehicleColor: this.vehicleColors[indexOfVehicleColor],
        yearOfVehicle: vehicle.vehicleYear,
        licenseNumber: vehicle.licenseNumber,
        vehicleStateCode: vehicle.stateCode,
        vehicleInsured: vehicle.insured === 'Y' ? true : false,
        electricVehicle: vehicle.electric,
        active: vehicle.active,
        printModel: vehicle.printModel
      });
    })
  }

  public closeModal() {
    this.isModalOpen = false;
  }

  public openRegisterModal() {
    this.isRegisterModalOpen = true;
  }

  public closeRegisterModal() {
    this.isRegisterModalOpen = false;
  }

  public registerDecal() {
    if (this.selectedDecal && this.decalNumber.length) {
      let decalToIssue: IDecalNumberRegistration = {
        decalTypeID: this.selectedDecal.decalId,
        decalNumber: parseInt(this.decalNumber),
        paidInPerson: this.paidInPerson ? 'Y' : undefined
      }
      this.vehicleDecalService.issueVehicleDecal(decalToIssue).subscribe(() => {
        this.isRegisterModalOpen = false;
        this.store.dispatch(clearVehicleDecalsAction());
      });
    }
  }

  public openExpirationModal() {
    this.isExpireModalOpen = true;
  }

  public closeExpirationModal() {
    this.isExpireModalOpen = false;
  }

  public expireDecal() {
    if (this.selectedDecal) {
      let decalToExpire: IDecalExpiration = { decalTypeId: this.selectedDecal.decalId, clearNumber: this.clearDecalNumber }
      this.vehicleDecalService.expireDecal(decalToExpire).subscribe(() => {
        this.isExpireModalOpen = false;
        this.store.dispatch(clearVehicleDecalsAction());
      });
    }
  }

  public setStatusText(decal: IVehicleDecal): string {
    let statusText: string = 'Status: ' + decal.dispositionLabel;

    if (decal.dispositionLabel === this.ISSUE_DISPOSITION) {
      statusText += ' -- Expires: ' + decal.decalEndDate;
    }

    return statusText;
  }
}
